// Single source of truth for tier prices. Any UI surface that displays
// or transacts on tier pricing MUST import from here — never re-declare
// the numbers in a component. The display copy (taglines, feature lists,
// badges) stays page-specific because marketing copy is intentionally
// different per surface; the numbers must never be.
//
// What this won't replace: Stripe Payment Link URLs (those live in
// src/lib/stripe.js, sourced from VITE_STRIPE_* env vars). The mapping
// between (tier, cycle) → Stripe URL happens server-side in
// /api/onboarding-complete.

export const TIERS = {
  trial: {
    id:           "trial",
    name:         "Trial",
    setupFee:     97,    // charged immediately at checkout
    days:         15,    // free trial length before auto-conversion
    convertsTo:   397,   // = Starter monthly
  },
  starter: {
    id:            "starter",
    name:          "Starter",
    monthly:       397,
    annualMonthly: 346,  // monthly-equivalent display value
    annualTotal:   4149, // the actual yearly charge
  },
  growth: {
    id:            "growth",
    name:          "Growth",
    monthly:       797,
    annualMonthly: 679,  // = round(8149/12)
    annualTotal:   8149,
  },
  scale: {
    id:            "scale",
    name:          "Scale",
    monthly:       1497,
    annualMonthly: 1262, // = round(15149/12)
    annualTotal:   15149,
  },
};

// Convenience iterables.
export const PAID_TIERS = [TIERS.starter, TIERS.growth, TIERS.scale];
export const ALL_TIERS  = [TIERS.trial, TIERS.starter, TIERS.growth, TIERS.scale];

// Picks the right amount + period label for a tier × cycle combo. Trial
// is always $97 for 15 days regardless of cycle (the underlying
// subscription is Starter monthly with a 15-day free trial; the visible
// $97 is the one-time setup fee).
export const priceLabelFor = (tierId, cycle) => {
  if (tierId === "trial") {
    return { amount: `$${TIERS.trial.setupFee}`, period: `for ${TIERS.trial.days} days` };
  }
  const t = TIERS[tierId];
  if (!t) return { amount: "", period: "" };
  if (cycle === "annual") {
    return { amount: `$${t.annualTotal.toLocaleString()}`, period: "per year, billed once" };
  }
  return { amount: `$${t.monthly}`, period: "per month" };
};
